import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import LeaveBalanceService, {
  type UserLeaveBalanceDto,
  type UserLeaveBalanceSummaryDto,
  type LeaveBalanceLedgerDto,
  type ManualBalanceAdjustmentDto,
  type GetLeaveBalanceSummaryParams,
  type GetLedgerParams,
} from "@/services/api/leaveBalance";
import { handleResponse } from "@/services/axios";

interface LeaveBalanceState {
  summaryList: UserLeaveBalanceSummaryDto[];
  userBalances: UserLeaveBalanceDto[];
  ledgerHistory: LeaveBalanceLedgerDto[];
  totalRecords: number;
  isLoading: boolean;
  isLedgerLoading: boolean;
  isAdjusting: boolean;
  error: string | null;
}

const initialState: LeaveBalanceState = {
  summaryList: [],
  userBalances: [],
  ledgerHistory: [],
  totalRecords: 0,
  isLoading: false,
  isLedgerLoading: false,
  isAdjusting: false,
  error: null,
};

export const fetchLeaveBalanceSummary = createAsyncThunk(
  "leaveBalance/fetchLeaveBalanceSummary",
  async (params: GetLeaveBalanceSummaryParams, { rejectWithValue }) => {
    try {
      const res = await LeaveBalanceService.getLeaveBalanceSummary(params);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || err?.message || "Failed to fetch leave balance summary"
      );
    }
  }
);

export const fetchUserLeaveBalance = createAsyncThunk(
  "leaveBalance/fetchUserLeaveBalance",
  async ({ userId, year }: { userId?: number; year?: number }, { rejectWithValue }) => {
    try {
      const res = await LeaveBalanceService.getUserLeaveBalance(userId, year);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || err?.message || "Failed to fetch user leave balance"
      );
    }
  }
);

export const fetchMyLeaveBalance = createAsyncThunk(
  "leaveBalance/fetchMyLeaveBalance",
  async ({ year }: { year?: number } = {}, { rejectWithValue }) => {
    try {
      const res = await LeaveBalanceService.getMyLeaveBalance(year);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || err?.message || "Failed to fetch my leave balance"
      );
    }
  }
);

export const fetchLeaveBalanceLedger = createAsyncThunk(
  "leaveBalance/fetchLeaveBalanceLedger",
  async (params: GetLedgerParams, { rejectWithValue }) => {
    try {
      const res = await LeaveBalanceService.getLeaveBalanceLedger(params);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || err?.message || "Failed to fetch leave balance ledger"
      );
    }
  }
);

export const fetchMyLeaveLedger = createAsyncThunk(
  "leaveBalance/fetchMyLeaveLedger",
  async (params: { leaveTypeId?: number; year?: number }, { rejectWithValue }) => {
    try {
      const res = await LeaveBalanceService.getMyLeaveLedger(params);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || err?.message || "Failed to fetch my leave ledger"
      );
    }
  }
);

export const adjustBalance = createAsyncThunk(
  "leaveBalance/adjustBalance",
  async (payload: ManualBalanceAdjustmentDto, { rejectWithValue }) => {
    try {
      const res = await LeaveBalanceService.adjustLeaveBalance(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || err?.message || "Failed to adjust leave balance"
      );
    }
  }
);

const leaveBalanceSlice = createSlice({
  name: "leaveBalance",
  initialState,
  reducers: {
    clearLedgerHistory: (state) => {
      state.ledgerHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLeaveBalanceSummary
      .addCase(fetchLeaveBalanceSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeaveBalanceSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        const payloadData = action.payload;
        if (Array.isArray(payloadData)) {
          state.summaryList = payloadData;
          state.totalRecords = payloadData.length;
        } else if (payloadData && typeof payloadData === "object") {
          state.summaryList = payloadData.Items || payloadData.Data || payloadData.data || payloadData.Summary || [];
          state.totalRecords = payloadData.TotalRecords ?? payloadData.TotalCount ?? payloadData.totalRecords ?? state.summaryList.length;
        } else {
          state.summaryList = [];
          state.totalRecords = 0;
        }
      })
      .addCase(fetchLeaveBalanceSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // fetchUserLeaveBalance & fetchMyLeaveBalance
      .addCase(fetchUserLeaveBalance.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserLeaveBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userBalances = Array.isArray(action.payload) ? action.payload : action.payload?.data || action.payload?.Items || [];
      })
      .addCase(fetchUserLeaveBalance.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchMyLeaveBalance.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMyLeaveBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userBalances = Array.isArray(action.payload) ? action.payload : action.payload?.data || action.payload?.Items || [];
      })
      .addCase(fetchMyLeaveBalance.rejected, (state) => {
        state.isLoading = false;
      })

      // fetchLeaveBalanceLedger & fetchMyLeaveLedger
      .addCase(fetchLeaveBalanceLedger.pending, (state) => {
        state.isLedgerLoading = true;
      })
      .addCase(fetchLeaveBalanceLedger.fulfilled, (state, action) => {
        state.isLedgerLoading = false;
        state.ledgerHistory = Array.isArray(action.payload) 
          ? action.payload 
          : action.payload?.Items || action.payload?.data || [];
      })
      .addCase(fetchLeaveBalanceLedger.rejected, (state) => {
        state.isLedgerLoading = false;
      })
      .addCase(fetchMyLeaveLedger.pending, (state) => {
        state.isLedgerLoading = true;
      })
      .addCase(fetchMyLeaveLedger.fulfilled, (state, action) => {
        state.isLedgerLoading = false;
        state.ledgerHistory = Array.isArray(action.payload) 
          ? action.payload 
          : action.payload?.Items || action.payload?.data || [];
      })
      .addCase(fetchMyLeaveLedger.rejected, (state) => {
        state.isLedgerLoading = false;
      })

      // adjustBalance
      .addCase(adjustBalance.pending, (state) => {
        state.isAdjusting = true;
      })
      .addCase(adjustBalance.fulfilled, (state) => {
        state.isAdjusting = false;
      })
      .addCase(adjustBalance.rejected, (state) => {
        state.isAdjusting = false;
      });
  },
});

export const { clearLedgerHistory } = leaveBalanceSlice.actions;
export default leaveBalanceSlice.reducer;
